import { Col, Row } from 'react-bootstrap';
import { Error } from '@progress/kendo-react-labels';
import { Button } from '@progress/kendo-react-buttons';
import React, { useCallback, useRef, useState, useEffect } from 'react';

import CameraModal from '../camera-modal';
import { uuid } from '../../../../types/common-helper';
import { GetFileExtensionsBySource } from '../../../../utils/game-document';

const generateRandomString = () => {
  return uuid().split('-').join('').substring(0, 5);
};

const AnswerVideo: React.FC = () => {
  const inputFile = useRef<HTMLInputElement>(null);
  const [blobImage, setBlobImage] = useState<string>('');
  const [isDisplayWebcam, setIsDisplayWebcam] = useState<boolean>(false);
  const [isValidFileExtension, setIsValidFileExtension] =
    useState<boolean>(true);
  const [facingMode, setFacingMode] = useState<string>('environment');
  const [videoKey, setVideoKey] = useState<number>(0);

  useEffect(() => {
    setVideoKey((prev) => prev + 1);
  }, []);

  const handleSelectImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const fileName = file.name;
      const fileExtension = fileName.slice(fileName.lastIndexOf('.'));
      const baseName = fileName.slice(0, fileName.lastIndexOf('.'));
      const newFileName = `${baseName}-${generateRandomString()}${fileExtension}`;

      setIsValidFileExtension(true);

      let extensionFound = undefined;

      if (fileExtension) {
        // set file ext checker
        let imageExtensionList = GetFileExtensionsBySource(
          'Assets - Task Content - Video Answer Type'
        );

        if (imageExtensionList) {
          extensionFound = imageExtensionList.find(
            (validFileExtension) =>
              validFileExtension ===
              fileExtension.replaceAll('.', '').toLowerCase()
          );
          setIsValidFileExtension(extensionFound ? true : false);
        }
      }

      if (extensionFound) {
        const newFile = new File([file], newFileName, { type: file.type });
        setBlobImage(URL.createObjectURL(newFile));
        setVideoKey((prev) => prev + 1);
      } else {
        setBlobImage('');
      }
    }
  };

  const onAddImageClick = () => inputFile.current && inputFile.current.click();

  const handleSwitchCamera = useCallback(() => {
    setFacingMode((prev) => (prev === 'user' ? 'environment' : 'user'));
  }, [setFacingMode]);

  const handleVideoCapture = useCallback((blob: Blob, url: string) => {
    const extension = blob.type.split('/')[1];
    const file = new File(
      [blob],
      `recorded-video-${generateRandomString()}.${extension}`,
      {
        type: blob.type
      }
    );
    setBlobImage(URL.createObjectURL(file));
    setVideoKey((prev) => prev + 1);
    setIsDisplayWebcam(false);
  }, []);

  return (
    <div id={'answer-file'} className={'d-flex flex-column flex-grow-1 h-100'}>
      <div
        className={
          'd-flex flex-grow-1 flex-column justify-content-center border text-center h-100 position-relative'
        }>
        <div
          className={'d-flex justify-content-center align-items-center h-100'}>
          {blobImage === '' ? (
            <div
              className={
                'd-flex flex-column align-items-center justify-content-center'
              }>
              <span
                className={'material-symbols-outlined text-success'}
                style={{
                  fontSize: '64px'
                }}>
                videocam
              </span>
              <span>Preview</span>
            </div>
          ) : (
            <video
              key={videoKey}
              controls
              preload={'auto'}
              width={'650'}
              height={'400'}
              className={'img-fluid img-thumbnail'}
              playsInline>
              <source src={blobImage} type={'video/mp4'} />
              <source src={blobImage} type={'video/webm'} />
              Your browser does not support the video tag.
            </video>
          )}
        </div>
      </div>

      <Row className={'py-2'}>
        {!isValidFileExtension && (
          <Col className={'mb-4'} md={12}>
            <Error>Invalid File Type: Please upload video files only.</Error>
          </Col>
        )}

        <div className={'d-flex flex-column gap-2'}>
          <Button
            className={'w-100 upload-button p-2'}
            onClick={() => setIsDisplayWebcam(true)}>
            <div
              className={
                'd-flex justify-content-center align-items-center gap-3'
              }>
              <span
                className={'material-symbols-outlined mr-2 text-success'}
                role={'button'}>
                videocam
              </span>
              <span className={'fw-normal'}>Take video</span>
            </div>
          </Button>
          <Button
            className={'w-100 upload-button p-2'}
            onClick={onAddImageClick}>
            <div
              className={
                'd-flex justify-content-center align-items-center gap-3'
              }>
              <span
                className={'material-symbols-outlined mr-2 text-success'}
                role={'button'}>
                add_photo_alternate
              </span>
              <span className={'fw-normal'}>Add from files</span>
            </div>
          </Button>
          <input
            type={'file'}
            id={'answer-file-upload'}
            className={'d-none'}
            onChange={handleSelectImage}
            onClick={(event) => {
              event.currentTarget.value = '';
            }}
            ref={inputFile}
            accept={'video/*'}
          />
        </div>
      </Row>

      <CameraModal
        isOpen={isDisplayWebcam}
        onClose={() => setIsDisplayWebcam(false)}
        onCapture={() => {}}
        onVideoCapture={handleVideoCapture}
        facingMode={facingMode}
        onSwitchCamera={handleSwitchCamera}
        mode="video"
      />
    </div>
  );
};

export default AnswerVideo;
